import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { UmraPackagesService, UmraPackageProperties } from '../Services/umra-packages.service';
import { UmraFlightSegmentProperties } from '../umra-custom-packages-add/umra-custom-packages-add.component'

@Component({
  selector: 'app-umra-package-management',
  templateUrl: './umra-package-management.component.html',
  styleUrls: ['./umra-package-management.component.css']
})
export class UmraPackageManagementComponent implements OnInit {

  minOutboundSegments = 1;
  minReturnSegments = 1;
  maxSegments = 6;

  // FormArray getters
  get editOutboundFlights(): FormArray {
    return this.editForm.get('outboundFlights') as FormArray;
  }

  get editReturnFlights(): FormArray {
    return this.editForm.get('returnFlights') as FormArray;
  }

  get editTotalSegments(): number {
    return this.editOutboundFlights.length + this.editReturnFlights.length;
  }

  get editCanAddSegment(): boolean {
    return this.editTotalSegments < this.maxSegments;
  }

  // Create a flight segment form group
  createEditFlightSegment(data: Partial<UmraFlightSegmentProperties>): FormGroup {
    return this.fb.group({
      airlineName: [data.airlineName || '', Validators.required],
      flightNo: [data.flightNo || '', Validators.required],
      routeStart: [data.routeStart || '', [Validators.required, Validators.maxLength(3)]],
      routeEnd: [data.routeEnd || '', [Validators.required, Validators.maxLength(3)]],
      travelDate: [data.travelDate || '', Validators.required],
      arrivalDate: [data.arrivalDate || '', Validators.required],
      etd: [data.etd || '00:00', Validators.required],
      eta: [data.eta || '00:00', Validators.required]
    });
  }

  // Add/Remove segments
  addEditOutboundSegment(): void {
    if (this.editTotalSegments >= this.maxSegments) return;
    let data = new UmraFlightSegmentProperties();
    this.editOutboundFlights.push(this.createEditFlightSegment(data));
  }

  addEditReturnSegment(): void {
    if (this.editTotalSegments >= this.maxSegments) return;
    let data = new UmraFlightSegmentProperties();
    this.editReturnFlights.push(this.createEditFlightSegment(data));
  }

  removeEditOutboundSegment(index: number): void {
    if (this.editOutboundFlights.length <= this.minOutboundSegments) return;
    this.editOutboundFlights.removeAt(index);
  }

  removeEditReturnSegment(index: number): void {
    if (this.editReturnFlights.length <= this.minReturnSegments) return;
    this.editReturnFlights.removeAt(index);
  }

  canRemoveEditOutbound(index: number): boolean {
    return this.editOutboundFlights.length > this.minOutboundSegments && index >= this.minOutboundSegments;
  }

  canRemoveEditReturn(index: number): boolean {
    return this.editReturnFlights.length > this.minReturnSegments && index >= this.minReturnSegments;
  }

  // Build route string
  buildRouteString(segments: UmraFlightSegmentProperties[]): string {
    if (!segments || segments.length === 0) return '';
    const routePoints: string[] = [];
    segments.forEach((segment, index) => {
      if (index === 0) {
        routePoints.push(segment.routeStart);
        routePoints.push(segment.routeEnd);
      } else {
        routePoints.push(segment.routeEnd);
      }
    });
    return routePoints.join('-');
  }

  // Data
  allPackages: UmraPackageProperties[] = [];
  filteredPackages: UmraPackageProperties[] = [];

  // Edit Mode
  editingPackage: UmraPackageProperties | null = null;
  editForm: FormGroup;

  // UI State
  loading = false;
  submitted = false;
  successMessage: string | null = null;
  error: string | null = null;

  // Filter
  statusFilter: string = 'all'; // 'all', 'active', 'inactive', 'custom'
  searchTerm: string = '';

  // Confirm Dialog
  showDeleteConfirm = false;
  packageToDelete: UmraPackageProperties | null = null;

  get activeCount(): number {
    return this.allPackages.filter(p => p.isActive).length;
  }

  get inactiveCount(): number {
    return this.allPackages.filter(p => !p.isActive).length;
  }

  get customCount(): number {
    return this.allPackages.filter(p => p.offerStatus === 'Custom').length;
  }

  constructor(private packageService: UmraPackagesService, private fb: FormBuilder)
  {
    this.editForm = this.fb.group({
      packageTitle: ['', Validators.required],
      nightsCount: ['', Validators.required],
      makkahHotel: ['', Validators.required],
      madinahHotel: ['', Validators.required],
      priceSharing: ['', Validators.required],
      priceDouble: ['', Validators.required],
      priceTriple: ['', Validators.required],
      priceQuad: ['', Validators.required],
      availableSeats: ['', Validators.required],
      badgeText: ['', Validators.required],
      groupCode: ['', Validators.required],
      packageCode: ['', Validators.required],
      provider: ['Tushmit Travel & Tours'],
      providerType: ['Custom'],
      discountPerPassenger: ['', Validators.required],
      serviceChargesPerPassenger: ['', Validators.required],
      makkahZayarat: [false],
      madinahZayarat: [false],
      taifZayarat: [false],
      // Flight segments
      outboundFlights: this.fb.array([]),
      returnFlights: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadPackages();
  }

  loadPackages(): void {
    this.loading = true;
    this.error = null;

    this.packageService.getAllPackages().subscribe({
      next: (response) => {
        if (response.success) {
          this.allPackages = response.umraPackagesList;
          this.applyFilters();
        } else {
          this.error = 'Failed to load packages';
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading packages:', error);
        this.error = 'Failed to load packages';
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    let result = [...this.allPackages];

    // Status filter
    if (this.statusFilter === 'active') {
      result = result.filter(p => p.isActive);
    } else if (this.statusFilter === 'inactive') {
      result = result.filter(p => !p.isActive);
    } else if (this.statusFilter === 'custom') {
      result = result.filter(p => p.offerStatus === 'Custom');
    }

    // Search
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(p =>
        p.packageTitle.toLowerCase().includes(term) ||
        p.airlineName.toLowerCase().includes(term) ||
        p.packageCode.toLowerCase().includes(term) ||
        p.groupCode.toLowerCase().includes(term)
      );
    }

    this.filteredPackages = result;
  }

  // EDIT PACKAGE
  startEdit(selectedPackage: UmraPackageProperties): void {
    this.editingPackage = selectedPackage;
    this.editForm.patchValue({
      packageTitle: selectedPackage.packageTitle,
      nightsCount: selectedPackage.nightsCount,
      makkahHotel: selectedPackage.makkahHotel,
      madinahHotel: selectedPackage.madinahHotel,
      priceSharing: selectedPackage.priceSharing,
      priceDouble: selectedPackage.priceDouble,
      priceTriple: selectedPackage.priceTriple,
      priceQuad: selectedPackage.priceQuad,
      availableSeats: selectedPackage.availableSeats,
      badgeText: selectedPackage.badgeText,
      groupCode: selectedPackage.groupCode,
      packageCode: selectedPackage.packageCode,
      provider: selectedPackage.provider,
      providerType: selectedPackage.providerType,
      serviceChargesPerPassenger: selectedPackage.serviceChargesPerPassenger,
      discountPerPassenger: selectedPackage.discountPerPassenger,
      makkahZayarat: selectedPackage.makkahZayarat,
      madinahZayarat: selectedPackage.madinahZayarat,
      taifZayarat: selectedPackage.taifZayarat
    });
    // Clear existing flight arrays
    while (this.editOutboundFlights.length) this.editOutboundFlights.removeAt(0);
    while (this.editReturnFlights.length) this.editReturnFlights.removeAt(0);

    // Parsed outbound segments from JSON or created from package fields
    if (selectedPackage.outboundJourney) {
      try {
        const segments = JSON.parse(selectedPackage.outboundJourney) as UmraFlightSegmentProperties[];
        segments.forEach(s => this.editOutboundFlights.push(this.createEditFlightSegment(s)));
      } catch {
        this.editOutboundFlights.push(this.createEditFlightSegment({
          airlineName: selectedPackage.airlineName,
          flightNo: selectedPackage.departureFligntNo,
          routeStart: selectedPackage.routeStart,
          routeEnd: selectedPackage.routeEnd,
          travelDate: new Date(selectedPackage.travelDate).toISOString().split('T')[0],
          arrivalDate: new Date(selectedPackage.arrivalDate).toISOString().split('T')[0],
          etd: selectedPackage.departureETD,
          eta: selectedPackage.departureETA
        }));
      }
    } else {
      // Create from existing package fields
      this.editOutboundFlights.push(this.createEditFlightSegment({
        airlineName: selectedPackage.airlineName,
        flightNo: selectedPackage.departureFligntNo,
        routeStart: selectedPackage.routeStart,
        routeEnd: selectedPackage.routeEnd,
        travelDate: new Date(selectedPackage.travelDate).toISOString().split('T')[0],
        arrivalDate: new Date(selectedPackage.arrivalDate).toISOString().split('T')[0],
        etd: selectedPackage.departureETD,
        eta: selectedPackage.departureETA
      }));
    }

    // Parse return segments
    if (selectedPackage.returnJourney) {
      try {
        const segments = JSON.parse(selectedPackage.returnJourney) as UmraFlightSegmentProperties[];
        segments.forEach(s => this.editReturnFlights.push(this.createEditFlightSegment(s)));
      } catch {
        this.editReturnFlights.push(this.createEditFlightSegment({
          airlineName: selectedPackage.airlineName,
          flightNo: selectedPackage.arrivalFligntNo,
          routeStart: selectedPackage.routeStart,
          routeEnd: selectedPackage.routeEnd,
          travelDate: selectedPackage.arrivalDate,
          arrivalDate: selectedPackage.arrivalDate,
          etd: selectedPackage.arrivalETD,
          eta: selectedPackage.arrivalETA
        }));
      }
    } else {
      this.editReturnFlights.push(this.createEditFlightSegment({
        airlineName: selectedPackage.airlineName,
        flightNo: selectedPackage.arrivalFligntNo,
        routeStart: selectedPackage.routeStart,
        routeEnd: selectedPackage.routeStart,
        travelDate: selectedPackage.travelDate,
        arrivalDate: selectedPackage.arrivalDate,
        etd: selectedPackage.arrivalETD,
        eta: selectedPackage.arrivalETA
      }));
    }

    // Scroll to form
    setTimeout(() => {
      document.getElementById('editFormSection').scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }
  
  cancelEdit(): void {
    this.editingPackage = null;
    this.editForm.reset();
    this.submitted = false;
  }

  saveEdit(): void {
    this.submitted = true;

    if (this.editForm.invalid) {
      this.editOutboundFlights.controls.forEach(c => c.markAllAsTouched());
      this.editReturnFlights.controls.forEach(c => c.markAllAsTouched());
      return;
    }

    if (!this.editingPackage) return;

    this.loading = true;
    const formData = this.editForm.value;
    const outboundFlights = formData.outboundFlights as UmraFlightSegmentProperties[];
    const returnFlights = formData.returnFlights as UmraFlightSegmentProperties[];

    const firstOutbound = outboundFlights[0];
    const lastOutbound = outboundFlights[outboundFlights.length - 1];
    const firstReturn = returnFlights[0];
    const lastReturn = returnFlights[returnFlights.length - 1];
    const updatedPackage: UmraPackageProperties = {
      travelDate: new Date(firstOutbound.travelDate).toISOString(),
      arrivalDate: new Date(firstReturn.travelDate).toISOString(),
      id: this.editingPackage.id,
      packageId: this.editingPackage.packageId,
      packageTitle: formData.packageTitle,
      airlineName: firstOutbound.airlineName,
      routeStart: firstOutbound.routeStart,
      routeEnd: lastOutbound.routeEnd,
      departureRoute: `${firstOutbound.routeStart}-${lastOutbound.routeEnd}`,
      returnRoute: `${firstReturn.routeStart}-${lastReturn.routeEnd}`,
      outboundJourney: JSON.stringify(outboundFlights),
      returnJourney: JSON.stringify(returnFlights),
      nightsCount: formData.nightsCount,
      makkahHotel: formData.makkahHotel,
      madinahHotel: formData.madinahHotel,
      priceSharing: parseFloat(formData.priceSharing) || 0,
      priceDouble: parseFloat(formData.priceDouble) || 0,
      priceTriple: parseFloat(formData.priceTriple) || 0,
      priceQuad: parseFloat(formData.priceQuad) || 0,
      availableSeats: formData.availableSeats,
      badgeText: formData.badgeText || 'Custom',
      groupCode: formData.groupCode || `CUSTOM-${Date.now()}`,
      packageCode: formData.packageCode || `CP-${Date.now()}`,
      provider: formData.provider,
      isActive: true,
      creationAgencyId: localStorage.getItem("AgencyId"),
      reservationAgencyId: '',
      creationUserId: parseInt(localStorage.getItem("UserId")),
      reservationUserId: 1,
      creationDate: new Date().toISOString(),
      offerStatus: 'Custom',
      approvalUserId: null,
      makkahHotelAddress: '',
      makkahHotelImage: '',
      makkahHotelMapLocation: '',
      madinahHotelAddress: '',
      madinahHotelImage: '',
      madinahHotelMapLocation: '',
      departureETD: firstOutbound.etd || '00:00',
      departureETA: lastOutbound.eta || '00:00',
      arrivalETD: firstReturn.etd || '00:00',
      arrivalETA: lastReturn.eta || '00:00',
      departureFligntNo: firstOutbound.flightNo,
      arrivalFligntNo: firstReturn.flightNo,
      discountPerPassenger: formData.discountPerPassenger || 0,
      serviceChargesPerPassenger: formData.serviceChargesPerPassenger || 0,
      customPackageId: formData.customPackageId,
      providerType: formData.providerType,
      supplierLetter: formData.supplierLetter,
      makkahZayarat: formData.makkahZayarat,
      madinahZayarat: formData.madinahZayarat,
      taifZayarat: formData.taifZayarat,
      markupOnSupplier: formData.serviceChargesPerPassenger || 0,
      discountOnSupplier: formData.discountPerPassenger || 0
    };
 
    this.packageService.updatePackage(updatedPackage).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = 'Package updated successfully!';
          this.cancelEdit();
          this.loadPackages();
          setTimeout(() => this.successMessage = null, 3000);
        } else {
          this.error = response.message || 'Failed to update package';
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error updating package:', error);
        this.error = 'Failed to update package';
        this.loading = false;
      }
    });
  }

  // TOGGLE STATUS
  toggleStatus(selectedPackage: UmraPackageProperties): void {
    const newStatus = !selectedPackage.isActive;
    const action = newStatus ? 'activate' : 'deactivate';

    if (!confirm(`Are you sure you want to ${action} this package?`)) {
      return;
    }

    this.packageService.togglePackageStatus(selectedPackage.packageId, newStatus).subscribe({
      next: (response) => {
        if (response.success) {
          selectedPackage.isActive = newStatus;
          this.successMessage = `Package ${action}d successfully!`;
          this.applyFilters();
          setTimeout(() => this.successMessage = null, 3000);
        } else {
          this.error = response.message || 'Failed to update status';
        }
      },
      error: (error) => {
        console.error('Error toggling status:', error);
        this.error = 'Failed to update package status';
      }
    });
  }

  // DELETE PACKAGE
  confirmDelete(selectedPackage: UmraPackageProperties): void {
    this.packageToDelete = selectedPackage;
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.packageToDelete = null;
    this.showDeleteConfirm = false;
  }

  executeDelete(): void {
    if (!this.packageToDelete) return;

    this.loading = true;
    this.packageService.deleteCustomPackage(this.packageToDelete.packageId).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = 'Package deleted successfully!';
          this.showDeleteConfirm = false;
          this.packageToDelete = null;
          this.loadPackages();
          setTimeout(() => this.successMessage = null, 3000);
        } else {
          this.error = response.message || 'Failed to delete package';
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error deleting package:', error);
        this.error = 'Failed to delete package';
        this.loading = false;
      }
    });
  }

  // HELPERS
  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getStatusBadge(pkg: UmraPackageProperties): string {
    if (!pkg.isActive) return 'badge bg-danger text-light';
    if (pkg.offerStatus === 'Custom') return 'badge bg-info';
    if (pkg.offerStatus === 'Deleted') return 'badge bg-danger text-light';
    return 'badge bg-success text-light';
  }

  getStatusText(pkg: UmraPackageProperties): string {
    if (!pkg.isActive) return 'Inactive';
    if (pkg.offerStatus === 'Custom') return 'Custom';
    if (pkg.offerStatus === 'Deleted') return 'Deleted';
    return 'Active';
  }

  get editF() {
    return this.editForm.controls;
  }

}
